import { useEffect, useState, useCallback } from "react";
import { ArrowRightLeft, Loader2, Users, Check, GraduationCap } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const selCls = "w-full h-10 rounded-lg border border-slate-200 bg-white px-2.5 text-sm focus:border-primary outline-none";

export default function DistrictTransfers() {
  const [schools, setSchools] = useState([]);
  const [from, setFrom] = useState({ school: "", grade: "" });
  const [to, setTo] = useState({ school: "", grade: "" });
  const [students, setStudents] = useState([]);
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [allowSchool, setAllowSchool] = useState(false);

  const toggleAllowSchool = async (v) => {
    setAllowSchool(v);
    try {
      await api.put("/corporate/transfer-settings", { allow_school_transfers: v });
      toast.success(v ? "School admins can now transfer students" : "Transfers are now district-managed only");
    } catch (e) {
      setAllowSchool(!v);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
    }
  };

  const load = useCallback(async () => {
    try {
      const [{ data }, st, lg] = await Promise.all([
        api.get("/corporate/transfer-options"),
        api.get("/corporate/transfer-settings"),
        api.get("/corporate/transfer-log"),
      ]);
      setSchools(data);
      setAllowSchool(st.data.allow_school_transfers);
      setLog(lg.data);
    } catch { /* no district */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!from.school) { setStudents([]); return; }
    let alive = true;
    api.get(`/corporate/school-students?company_id=${from.school}&class_id=${from.grade}`)
      .then(({ data }) => {
        if (!alive) return;
        setStudents(data);
        setChecked(Object.fromEntries(data.map((s) => [s.id, true])));
      }).catch(() => {});
    return () => { alive = false; };
  }, [from.school, from.grade]);

  if (schools.length < 2) return null;

  const fromSchool = schools.find((s) => s.id === from.school);
  const toSchool = schools.find((s) => s.id === to.school);
  const selectedIds = students.filter((s) => checked[s.id]).map((s) => s.id);

  const transfer = async () => {
    if (!window.confirm(`Transfer ${selectedIds.length} student(s) to ${toSchool?.name}? Car numbers will be re-assigned and bus assignments cleared.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/corporate/transfer", {
        student_ids: selectedIds, to_company_id: to.school, to_class_id: to.grade || "",
      });
      toast.success(`${data.transferred} student(s) transferred to ${toSchool?.name}`);
      setFrom({ school: "", grade: "" });
      setTo({ school: "", grade: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Transfer failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="corporate-transfers-section">
      <div className="mb-4">
        <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">Student transfers</h2>
        <p className="text-sm text-slate-600">Move students between district schools — e.g., graduating 5th grade to middle school. Parents, photos and pickup contacts move with them.</p>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 shadow-sm space-y-4">
        <label className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 cursor-pointer" data-testid="allow-school-transfers-toggle">
          <div>
            <div className="text-sm font-semibold text-slate-900">School admins can transfer students</div>
            <div className="text-[11px] text-slate-500">When on, each school's admin gets a "Transfer" action on their Students page to move a student to another district school</div>
          </div>
          <Switch checked={allowSchool} onCheckedChange={toggleAllowSchool} data-testid="allow-school-transfers-switch" />
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-3">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">From</div>
            <select value={from.school} onChange={(e) => setFrom({ school: e.target.value, grade: "" })} data-testid="transfer-from-school" className={selCls}>
              <option value="">Pick source school…</option>
              {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {fromSchool && (
              <select value={from.grade} onChange={(e) => setFrom({ ...from, grade: e.target.value })} data-testid="transfer-from-grade" className={selCls}>
                <option value="">All grades</option>
                {fromSchool.grades.map((g) => <option key={g.id} value={g.id}>{g.name} ({g.count})</option>)}
              </select>
            )}
          </div>
          <div className="space-y-3">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">To</div>
            <select value={to.school} onChange={(e) => setTo({ school: e.target.value, grade: "" })} data-testid="transfer-to-school" className={selCls}>
              <option value="">Pick destination school…</option>
              {schools.filter((s) => s.id !== from.school).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {toSchool && (
              <select value={to.grade} onChange={(e) => setTo({ ...to, grade: e.target.value })} data-testid="transfer-to-grade" className={selCls}>
                <option value="">No grade yet (assign later)</option>
                {toSchool.grades.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            )}
          </div>
        </div>

        {students.length > 0 && (
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between bg-slate-50 px-4 py-2 text-xs text-slate-600">
              <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {selectedIds.length} of {students.length} selected</span>
              <button onClick={() => {
                const all = selectedIds.length === students.length;
                setChecked(Object.fromEntries(students.map((s) => [s.id, !all])));
              }} data-testid="transfer-toggle-all" className="font-medium text-primary hover:underline">
                {selectedIds.length === students.length ? "Unselect all" : "Select all"}
              </button>
            </div>
            <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
              {students.map((s) => (
                <label key={s.id} className="flex items-center gap-3 px-4 py-2 text-sm text-slate-800 hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={!!checked[s.id]} onChange={(e) => setChecked({ ...checked, [s.id]: e.target.checked })}
                         data-testid={`transfer-student-${s.id}`} className="h-4 w-4 accent-[var(--primary,#1d4ed8)]" />
                  {s.full_name}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={transfer}
            disabled={busy || !to.school || selectedIds.length === 0}
            data-testid="transfer-submit-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:shadow disabled:opacity-50 transition"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRightLeft className="h-4 w-4" />}
            Transfer {selectedIds.length > 0 ? `${selectedIds.length} student${selectedIds.length === 1 ? "" : "s"}` : "students"}
          </button>
        </div>

        {log.length > 0 && (
          <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="transfer-history">
            <div className="bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
              Transfer history
            </div>
            <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
              {log.map((r) => (
                <div key={r.id} className="px-4 py-2.5 text-sm flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid={`transfer-log-${r.id}`}>
                  <span className="font-semibold text-slate-900">{r.student_name}</span>
                  <span className="text-slate-500">{r.from_school}</span>
                  <ArrowRightLeft className="h-3 w-3 text-slate-300" />
                  <span className="text-slate-700">{r.to_school}{r.to_grade ? ` · ${r.to_grade}` : ""}</span>
                  <span className="ml-auto text-[11px] text-slate-400">
                    by {r.by_name || r.by_role} · {new Date(r.at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
